// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Search } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import StaffListTable from '@/components/admin/staff/StaffListTable';
import AddEditStaffModal from '@/components/admin/staff/AddEditStaffModal';

interface StaffMember {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  status: string;
  created_at: string;
  event_count: number;
  permissions?: string[];
}

const StaffManagement: React.FC = () => {
  const { profile } = useAuth();
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [filteredStaff, setFilteredStaff] = useState<StaffMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<StaffMember | null>(null);

  const fetchStaff = async () => {
    if (!profile?.organization_id) return;

    try {
      setIsLoading(true);
      
      // Fetch staff with event assignment counts and permissions
      const { data: staffData, error: staffError } = await supabase
        .from('event_staff')
        .select(`
          id,
          name,
          email,
          phone,
          status,
          created_at,
          event_staff_assignments(permissions)
        `)
        .eq('organization_id', profile.organization_id)
        .order('created_at', { ascending: false });

      if (staffError) throw staffError;

      const formattedStaff = (staffData || []).map((member: any) => {
        // Get unique permissions across all assignments
        const allPerms = new Set<string>();
        member.event_staff_assignments?.forEach((assignment: any) => {
          assignment.permissions?.forEach((p: string) => allPerms.add(p));
        });

        return {
          id: member.id,
          name: member.name,
          email: member.email,
          phone: member.phone,
          status: member.status,
          created_at: member.created_at,
          event_count: member.event_staff_assignments?.length || 0,
          permissions: Array.from(allPerms),
        };
      });

      setStaff(formattedStaff);
      setFilteredStaff(formattedStaff);
    } catch (error: any) {
      console.error('Error fetching staff:', error);
      toast.error('Failed to load staff members');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();
  }, [profile?.organization_id]);

  useEffect(() => {
    let filtered = staff;

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(s => s.status === statusFilter);
    }

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(s => 
        s.name.toLowerCase().includes(query) ||
        s.email.toLowerCase().includes(query) ||
        (s.phone && s.phone.toLowerCase().includes(query))
      );
    }

    setFilteredStaff(filtered);
  }, [staff, statusFilter, searchQuery]);

  const handleAddStaff = () => {
    setSelectedStaff(null);
    setIsAddEditModalOpen(true);
  };

  const handleEditStaff = (staffMember: StaffMember) => {
    setSelectedStaff(staffMember);
    setIsAddEditModalOpen(true);
  };

  const handleDeleteStaff = async (staffId: string) => {
    if (!confirm('Are you sure you want to delete this staff member? This will remove all their event assignments.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('event_staff')
        .delete()
        .eq('id', staffId);

      if (error) throw error;

      toast.success('Staff member deleted successfully');
      fetchStaff();
    } catch (error: any) {
      console.error('Error deleting staff:', error);
      toast.error('Failed to delete staff member');
    }
  };

  const handleModalClose = (shouldRefresh: boolean) => {
    setIsAddEditModalOpen(false);
    setSelectedStaff(null);
    if (shouldRefresh) {
      fetchStaff();
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold text-primary-700">Staff Management</h1>
        <p className="text-muted-foreground">Manage event staff and their permissions</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Staff Members</CardTitle>
            <Button onClick={handleAddStaff}>
              <Plus className="h-4 w-4 mr-2" />
              Add Staff
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <StaffListTable
            staff={filteredStaff}
            isLoading={isLoading}
            onEdit={handleEditStaff}
            onDelete={handleDeleteStaff}
          />
        </CardContent>
      </Card>

      <AddEditStaffModal
        isOpen={isAddEditModalOpen}
        onClose={handleModalClose}
        staffMember={selectedStaff}
        organizationId={profile?.organization_id || ''}
      />
    </div>
  );
};

export default StaffManagement;
